import { I18nThemed } from 'interfaces/mixins';
import { DNode } from '@dojo/framework/core/interfaces';
import { v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/mixins/Themed';
import Button from '@dojo/widgets/button';

// import { publish } from 'pub-sub';

import { init_valueMap, formValueMap } from '../support/form-value-map';

import { FormLabel } from './form-label';
import { FormLikert } from './form-likert';
import { FormSelection } from './form-selection';
import { FormString } from './form-string';
import { FormText } from './form-text';

import rcssvc from '../support/rcs-svc';
import svc from '../support/svc';

import { FormFolder as IFormFolder, HeadlessBase } from '../interfaces';
import css from '../css/form.m.css';

import { lccstore, userstore, rcslocationChangeProcess } from 'lcc-state';
import { sitestatusChange } from 'lcc-state/LccStore';

export interface FormFolderProperties extends IFormFolder {
	baseurl?: string;
}

const formActions: { [key: string]: any } = {
	FormTextField: FormText,
	FormStringField: FormString,
	FormSelectionField: FormSelection,
	FormLikertField: FormLikert,
	FormLabelField: FormLabel
};

const skiptype = new Set(['FormMailerAdapter', 'FormThanksPage', 'Document']);

const idFromAtId = (v: string) => {
	const parts = v.split('/');
	return parts[parts.length - 1];
};

@theme(css)
export class FormFolder extends I18nThemed<FormFolderProperties> {
	formPromises: Promise<DNode>[] = [];
	formThanksPromise: Promise<HeadlessBase | null> = Promise.resolve(null);
	resolvedWidgets: DNode[] = [];
	formdata: { [key: string]: any } = {};

	protected render_prologue(): DNode {
		const { formPrologue } = this.properties;
		if (!formPrologue || formPrologue.data.length === 0) {
			return null;
		}
		return v('div', { classes: [this.theme(css.prologue)], innerHTML: formPrologue.data });
	}

	protected render_epilogue(): DNode {
		const { formEpilogue } = this.properties;
		if (!formEpilogue || formEpilogue.data.length === 0) {
			return null;
		}
		return v('div', { classes: [this.theme(css.prologue)], innerHTML: formEpilogue.data });
	}

	protected async route_to_thanks(): Promise<void> {
		const thanks = await this.formThanksPromise;
		const portalurl = window.hcstatic.portal_url!;
		const ploneurl = portalurl.replace(window.hcstatic.sitealias!, window.hcstatic.portal_site!);
		const { baseurl } = this.properties;
		const rcshome = baseurl?.slice(1) || 'common/rc';
		const fullurl = thanks !== null ? thanks['@id'] : rcshome;
		let url = fullurl.startsWith(ploneurl) ? fullurl.slice(ploneurl.length) : null;
		if (url === null) {
			url = fullurl;
		} else {
			url = url.split('/').slice(2).join('/');
		}
		console.log(`routing to ${url}`);
		if (url.startsWith('common')) {
			rcslocationChangeProcess(userstore)({ baseurl: `${baseurl}`, location: `/${url}` });
			// publish(`site/${url}`);
		}
	}

	generate_form(items: HeadlessBase[]): void {
		if (this.formPromises.length === 0) {
			init_valueMap();
			const urlparts = /^(https?):\/\/([a-z.-]*)\//.exec(window.hcstatic.portal_url!);
			const urischeme = urlparts![1];
			const hostname = `${urlparts![2]}`;

			this.formPromises = items.map((item) => {
				const subid = item['@id'];
				const fieldID = idFromAtId(subid);
				const widgetType = item['@type'];
				if (skiptype.has(widgetType)) {
					if (widgetType === 'FormThanksPage') {
						const getThanks = async (): Promise<any> => {
							const rest = /^.*\/common\/(.*)$/.exec(subid)![1];
							const data = await rcssvc.load_path({ rest, hostname, urischeme });
							console.log(`FormThanksPage`, data);
							return data;
						};
						this.formThanksPromise = getThanks();
					}
					return Promise.resolve(null);
				}
				const rest = /^.*\/common\/(.*)$/.exec(subid)![1];
				const getItem = async (): Promise<DNode> => {
					const data = await rcssvc.load_path({ rest, hostname, urischeme });
					const itemtype = data['@type'];
					if (formActions[itemtype]) {
						this.formdata[fieldID] = null;
						data.formdata = this.formdata;
						return w(formActions[itemtype], data);
					} else {
						return v('p', { styles: { color: 'red' } }, [`No widget defined for ${itemtype}`]);
					}
				};
				return getItem();
			});

			Promise.all(this.formPromises).then((widgets) => {
				widgets.forEach((widget) => {
					this.resolvedWidgets.push(widget);
				});
				this.invalidate();
			});
		}
	}

	protected render_form(): DNode {
		const { submitLabel, items } = this.properties;
		const formID = idFromAtId(this.properties['@id']);
		const action = formID === 'quick-comment' ? 'quick_comment' : 'evaluation';

		let kids: DNode[];
		if (this.resolvedWidgets.length === 0) {
			this.formdata = {};
			this.generate_form(items);
			kids = [v('div', {}, [v('i', { classes: ['fa'] })])];
		} else {
			kids = this.resolvedWidgets;
		}
		return v(
			'form',
			{
				classes: [this.theme(css.formbody)],
				onsubmit: (ev) => {
					ev && ev.stopPropagation();
					ev && ev.preventDefault();
					if (action === 'evaluation') {
						// flag evaluation complete
						const keyprops = () => {
							const { get, path } = lccstore;
							return {
								status: get(path('sitestatus'))
							};
						};
						const { status } = keyprops();
						status.eval_done = true;

						sitestatusChange({ sitestatus: status });
					}
					const { formdata } = this;
					// add usual set of required fields
					formdata.user_language = formValueMap.user_language;
					formdata.product = formValueMap.product;
					formdata.product_title = formValueMap.product_title;

					console.log(`${formID} formdata =`, formdata);
					const send = async () => await svc[action]({ formdata });
					send();
					this.route_to_thanks();
				}
			},
			[...kids, w(Button, { type: 'submit' }, [submitLabel])]
		);
	}

	protected render(): DNode {
		const { title } = this.properties;
		return v('div', { classes: [this.theme(css.root)] }, [
			v('h1', { classes: [this.theme(css.heading)] }, [title]),
			this.render_prologue(),
			this.render_form(),
			this.render_epilogue()
		]);
	}
}
